import { Button } from '@freelancer-os/ui/src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@freelancer-os/ui/src/components/ui/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ClientDetailBundle } from '@freelancer-os/shared-types';

const STATUS_STYLES: Record<string, string> = {
  PAID: 'bg-green-100 text-green-700 border border-green-200',
  PARTIAL: 'bg-blue-100 text-blue-700 border border-blue-200',
  PENDING: 'bg-amber-100 text-amber-700 border border-amber-200',
  OVERDUE: 'bg-red-100 text-red-700 border border-red-200',
  DRAFT: 'bg-surface-100 text-surface-600 border border-surface-200',
  CANCELLED: 'bg-surface-100 text-surface-500 border border-surface-200',
};

function formatVND(amount: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('vi-VN');
}

interface InvoicesTabProps {
  invoices: ClientDetailBundle['invoices'];
  clientId: string;
}

export function InvoicesTab({ invoices, clientId }: InvoicesTabProps) {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();

  if (invoices.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-surface-500 mb-4">{t('invoices.empty')}</p>
        <Button onClick={() => navigate(`/invoices/new?clientId=${clientId}`)}>
          {t('invoices.createFirst')}
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('invoices.code')}</TableHead>
            <TableHead className="text-right">{t('invoices.amount')}</TableHead>
            <TableHead>{t('invoices.status')}</TableHead>
            <TableHead>{t('invoices.due')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv) => (
            <TableRow
              key={inv.id}
              className="cursor-pointer"
              onClick={() => navigate(`/invoices/${inv.id}`)}
            >
              <TableCell className="font-mono text-sm">{inv.invoiceCode}</TableCell>
              <TableCell className="text-right font-medium">{formatVND(inv.totalAmount)}</TableCell>
              <TableCell>
                <span
                  className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    STATUS_STYLES[inv.status] ?? STATUS_STYLES.DRAFT
                  }`}
                >
                  {inv.status}
                </span>
              </TableCell>
              <TableCell className="text-sm text-surface-600">{formatDate(inv.dueDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
